import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import dayjs from 'dayjs';

import { PAGE_SIZE_SM } from '~constants/common';

import { formatDefaultSearchDate } from '~libs/dayjs';
import { useSearch } from '~libs/searchParams';

import { ClickableIcon, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import { faMagnifyingGlass } from '~components/icon';
import { TableView } from '~components/table-view-v3';
import type { SortState } from '~components/table-view-v3';

import budgetServiceColumns from '~support-at-home/forms/budget-services/list';
import { searchFields } from '~support-at-home/forms/budget-services/search';

import { getAll } from '~support-at-home/actions/budgetServices';

import {
  selectBudgetServices,
  selectBudgetServicesCount,
  selectBudgetServicesLoading,
  selectBudgetServicesTotal,
} from '~support-at-home/selectors/budgetServices';

interface ComponentProps {
  isActive: boolean;
}

const BudgetServices: React.FC<ComponentProps> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectBudgetServices);
  const budgetServicesLoading = useAppSelector(selectBudgetServicesLoading);
  const budgetServicesTotal = useAppSelector(selectBudgetServicesTotal);
  const budgetServicesCount = useAppSelector(selectBudgetServicesCount);

  const isLoading = budgetServicesLoading === 'pending';

  const initialSearchState: any = {
    start_date: formatDefaultSearchDate(dayjs()),
  };

  const initialSortState: SortState = [
    {
      id: 'start_date',
      direction: 'asc',
    },
  ];

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('budget-services-search', initialSearchState);
  const [params, setParams] = useState({
    sort: initialSortState.map(({ id, direction }) => `${id}.${direction ?? 'asc'}`),
    limit: PAGE_SIZE_SM,
  });

  const load = (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };

    // Keep search and load params separate as search can be cleared. Don't want to retain values when nothing is passed back.
    dispatch(getAll({ ...newParams, ...searchParams }));
    setParams(newParams);
  };

  useEffect(() => {
    if (isActive) {
      load();
    }
  }, [isActive, searchParams]);

  const sort = (sortState: SortState = []) => {
    load({ sort: sortState.map(({ id, direction }) => `${id}.${direction ?? 'asc'}`) });
  };

  const search = (values: { [key: string]: any }) => setSearchParams(values);

  return (
    <div>
      <Toolbar
        id="budget-services"
        rightCluster={
          <ClickableIcon
            className="me-2"
            icon={faMagnifyingGlass}
            id="invoice-search-button"
            onClick={() => setSearchBarOpen(!searchBarOpen)}
            toolTip="Toggle Search Bar"
          />
        }
      />
      <SearchBar fields={searchFields} onSubmit={search} initialValues={initialSearchState} isOpen={searchBarOpen} />
      <div data-testid="total-count" className="text-muted small py-1 px-2">
        Showing {budgetServicesCount} of {budgetServicesTotal} records
      </div>
      <TableView
        id="budget-services"
        columns={budgetServiceColumns}
        data={rows}
        allowSort={true}
        onSort={sort}
        isLoading={isLoading}
        allowPagination={budgetServicesCount < budgetServicesTotal}
        onLoadMore={() => load({ limit: params.limit + PAGE_SIZE_SM })}
        totalCount={budgetServicesTotal}
      />
    </div>
  );
};

export default BudgetServices;
